/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";
modulum("VMApplication", ["EventListenerNativeClass"], function (context, cls) {
  /**
   * Object that represents an application of a VM Session
   * @class VMApplication
   * @memberOf classes
   * @extends classes.EventListenerNativeClass
   * @publicdoc Base
   */
  class VMApplication extends cls.EventListenerNativeClass /** @lends classes.VMApplication.prototype */ {
    /* jshint ignore:start */
    static styleListLoaded = "gStyleListLoaded";
    /* jshint ignore:end */

    /**
     *
     * @param {classes.VMApplicationInfo} info - application info
     * @param {classes.VMSession} session
     */
    constructor(info, session) {
      super();

      /** @type {string|null} */
      this.procId = null;
      /** @type {boolean} */
      this.running = false;
      /** Indicator to know if the application is ending */
      this.ending = false;
      this.ended = false;
      /** Indicator to know if the application has protocol error */
      this.hasError = false;
      /** @type {boolean} */
      this.styleListsChanged = false;

      /** @type {Object} */
      this.usedStyleAttributes = {};

      this._processingDelayer = 0;

      this._title = "";
      this._icon = "";
      /** @type {Boolean} */
      this._windowListVisible = true;

      /**
       * @type {classes.VMSession}
       */
      this._session = session;
      /**
       * General information for this application
       * @type classes.VMApplicationInfo
       */
      this.applicationInfo = info;
      /**
       * The unique identifier of the application
       * @type {number}
       */
      this.applicationHash = session.getApplicationIdentifier();
      if (!info.inNewWindow) {
        /**
         * application ui
         * @type classes.UIApplicationService
         * */
        this._ui = cls.ApplicationServiceFactory.create("UI", this);
        /**
         * dvm management
         * @type classes.DVMApplicationService
         */
        this.dvm = cls.ApplicationServiceFactory.create("Dvm", this);
        /**
         * model (aui) management
         * @type classes.AuiApplicationService
         */
        this.model = cls.ApplicationServiceFactory.create("Model", this);
        /**
         * layout management
         * @type classes.LayoutApplicationService
         */
        this.layout = cls.ApplicationServiceFactory.create("Layout", this);
        /**
         * actions
         * @type classes.ActionApplicationService
         */
        this.action = cls.ApplicationServiceFactory.create("Action", this);
        /**
         * file transfer management
         * @type classes.FileTransferApplicationService
         */
        this.filetransfer = cls.ApplicationServiceFactory.create("FileTransfer", this);
        /**
         * Scheduler
         * @type classes.SchedulerApplicationService
         */
        this.scheduler = cls.ApplicationServiceFactory.create("Scheduler", this);
        /**
         * input management
         * @type classes.InputApplicationService
         */
        this.input = cls.ApplicationServiceFactory.create("Input", this);
        /**
         * Focus management
         * @type classes.FocusApplicationService
         */
        this.focus = cls.ApplicationServiceFactory.create("Focus", this);
        /**
         * Message management
         * @type classes.MessageApplicationService
         */
        this.message = cls.ApplicationServiceFactory.create("Message", this);
        /**
         * Notifications management
         * @type {classes.NotificationsApplicationService}
         */
        this.notifications = cls.ApplicationServiceFactory.create("Notifications", this);

        // emit gbcReady signal when gbc seems to be ready for user interaction
        this._afterLayoutHandler = this.layout.afterLayout(
          function () {
            // afterLayout can now fire while the VM is still processing (the layout runs between AUI
            // batches during a refresh() loop), so gbcReady must additionally wait for the VM to be idle.
            if (this.scheduler.hasNoCommandToProcess() && !this.isProcessing()) {
              this._registerTimeout(
                function () {
                  this.emit(context.constants.widgetEvents.gbcReady);
                }.bind(this),
                50
              );
            }
          }.bind(this),
          false
        );
        gbc.QAService.bindToScheduler(this.scheduler);
      }
      /**
       * protocolInterface
       * @type classes.ProtocolInterface
       */
      this.protocolInterface = cls.ApplicationServiceFactory.create(this._getProtocolInterface(info), this);

      this.styleAttributesChanged = [];
      context.WidgetService.registerVMApplication(this);

      //We must notify native app that the app is ready to obtain the focus on the webview
      this.gbcReady(function () {
        context.__wrapper.nativeCall(
          context.__wrapper.param(
            {
              name: "gbcReady",
              args: {
                windowState: gbc.HostService.getWindowState(),
                windowSize: [this.getNode(0).attribute("width"), this.getNode(0).attribute("height")],
                forceDefaultSettings: gbc.HostService.getForceDefaultSettings()
              }
            },
            this
          )
        );
      }, true);
    }
    /**
     * Get the owning session
     * @returns {classes.VMSession} The owning session
     * @publicdoc
     */
    getSession() {
      return this._session;
    }

    prepareEmergencyClose() {
      this._emergencyClose = cls.UANetwork._prepared.close(this);
      window.requestAnimationFrame(
        function () {
          try {
            if (window.opener && window.opener._emergencyClose) {
              window.opener._emergencyClose[context.uid].push(this._emergencyClose);
            }
          } catch (e) {
            if (e.name === "SecurityError") {
              context.LogService.networkProtocol.log("GBC opened from a cross domain origin", e);
            }
          }
        }.bind(this)
      );
    }
    _getProtocolInterface(info) {
      let result = "NoProtocolInterface";
      switch (info.mode) {
        case "direct":
          result = "DirectProtocolInterface";
          break;
        case "ua":
          result = "UAProtocolInterface";
          break;
        default:
          break;
      }
      return result;
    }
    /**
     * @inheritDoc
     */
    destroy() {
      if (!this._destroyed) {
        if (this._afterLayoutHandler) {
          this._afterLayoutHandler();
          this._afterLayoutHandler = null;
        }

        this._ui.destroy();
        this.filetransfer.destroy();
        this._session.remove(this);
        this.model.destroy();

        this.applicationInfo = null;

        this._session = null;
        this._ui = null;
        this.dvm = null;
        this.model = null;
        this.layout = null;
        this.action = null;
        this.filetransfer = null;
        this.scheduler = null;
        this.input = null;
        this.focus = null;
        this.message = null;
        this.protocolInterface = null;

        super.destroy();

        context.WidgetService.unregisterVMApplication(this);
      }
    }

    start() {
      this.protocolInterface.start(this.applicationInfo);
    }
    stop(message) {
      if (this.stopping) {
        return;
      }
      this.stopping = true;
      if (this.ended || !this.applicationInfo) {
        return;
      }
      if (message) {
        this.applicationInfo.ending = cls.ApplicationEnding.notok(message);
      }
      if (!this.applicationInfo.ending) {
        this.applicationInfo.ending = cls.ApplicationEnding.ok;
      }
      if (this.applicationInfo.urlParameters && this.applicationInfo.urlParameters.logPlayer) {
        this.applicationInfo.ending = cls.ApplicationEnding.logPlayer;
      }

      context.styler.bufferize();
      // TODO reorder, why we don't call this in destroy ??
      this.scheduler.destroy();
      this.model.remove();
      this.setEnding();
      this.action.destroy();
      this.layout.destroy();
      this.model.stop();
      this.dvm.destroy();
      this.protocolInterface.destroy();
      this.input.destroy();
      this.focus.destroy();
      this.filetransfer.destroy();
      this.message.destroy();

      this.destroy();
      context.styler.flush();
      this.ended = true;
    }
    /**
     * Set status of application
     * @param {boolean} running Status
     */
    setRunning(running) {
      this.running = running;
      this._ui.setRunning(running);
    }

    /**
     * Set the error status at application's protocol error
     */
    setError() {
      this.hasError = true;
    }

    /**
     * Set the ending status at application's end
     */
    setEnding() {
      if (!this.ending && !this.ended && !this._destroyed) {
        this.ending = true;
        this.setIdle();
      }
    }

    /**
     * Returns this application's info.
     * @returns {classes.VMApplicationInfo}
     */
    info() {
      return this.applicationInfo;
    }
    /**
     * Get application instantiated node by its Aui ID
     * @param {number} id the node id
     * @returns {classes.NodeBase} the node, if found
     * @publicdoc
     */
    getNode(id) {
      return this.model && this.model.getNode(id);
    }
    uiNode() {
      return this.getNode(0);
    }

    /**
     * Check if a widget has the VM focus
     * @param {classes.WidgetBase} widget
     * @returns {boolean} true if widget has the VM focus
     */
    hasVMFocus(widget) {
      const widgetNode = this.getNode(widget.getAuiTag());
      return widgetNode?.hasVMFocus() === true;
    }

    /**
     * Get the VM Focused Node instance
     * @returns {classes.NodeBase} The VM focused node
     * @publicdoc
     */
    getFocusedVMNode() {
      const uiNode = this.uiNode();
      if (uiNode) {
        const id = uiNode.attribute("focus");
        return this.getNode(id);
      }
      return null;
    }

    /**
     * Get the VM Focused Node instance
     * or if the focused node is a table or a matrix get the current value node
     * @param {boolean} [inputModeOnly] - return value node only if is node is in INPUT mode
     * @returns {*|classes.NodeBase}
     */
    getFocusedVMNodeAndValue(inputModeOnly) {
      let focusedNode = this.getFocusedVMNode();
      if (focusedNode && focusedNode.getCurrentValueNode) {
        const currentValueNode = focusedNode.getCurrentValueNode(inputModeOnly);
        if (currentValueNode) {
          focusedNode = currentValueNode;
        }
      }
      return focusedNode;
    }

    newTask() {
      let session = context.BrowserWindowsService.getRootSession();
      if (session) {
        session.newTask();
      } else {
        this.protocolInterface.newTask();
      }
    }
    /**
     * Set the idle status to true
     */
    setIdle() {
      this._setProcessingStyle(false);
      this.dvm.setIdle(true);
      this.action.setInterruptablesActive(false);
    }

    /**
     * Set the processing status to true
     */
    setProcessing() {
      this._setProcessingStyle(true);
      this.dvm.setIdle(false);
      this.action.setInterruptablesActive(true);
    }

    _setProcessingStyleImpl(processing) {
      this._processingDelayer = 0;
      if (this._ui && this._ui.getWidget()) {
        const windows = this.model.getNodesByTag("Window"),
          len = windows.length;
        let i = 0;
        for (; i < len; i++) {
          if (windows[i] && windows[i]._setProcessingStyle) {
            windows[i]._setProcessingStyle(processing);
          }
        }
      }
    }

    _setProcessingStyle(processing) {
      if (!processing) {
        if (this._processingDelayer !== 0) {
          this._clearAnimationFrame(this._processingDelayer);
          this._processingDelayer = 0;
        }
        this._setProcessingStyleImpl(false);
      } else {
        if (this._processingDelayer === 0) {
          this._processingDelayer = this._registerAnimationFrame(this._setProcessingStyleImpl.bind(this, true));
        }
      }
    }
    /**
     * Check if the application is idle
     * @returns {boolean} true if idle, false otherwise
     */
    isIdle() {
      return this.dvm.idle;
    }

    /**
     * Check if the application is running
     * @returns {boolean} true if running, false otherwise
     */
    isProcessing() {
      return !this.dvm?.idle;
    }
    /**
     * Empty scheduler commands queue and send an interrupt order
     */
    interrupt() {
      this.scheduler.emptyCommandsQueue();
      this.protocolInterface.interrupt();
    }
    close() {
      if (!this.ended && !this._destroyed) {
        if (this.ending) {
          this.destroy();
        } else {
          this.protocolInterface.close();
        }
      }
    }
    error() {
      this.setEnding();
    }
    /**
     * fail application gracefully,
     * @param ending ending message
     */
    fail(ending) {
      if (ending && this.applicationInfo) {
        this.applicationInfo.ending = cls.ApplicationEnding.notok(ending);
      }
      this._registerTimeout(
        function () {
          this.protocolInterface.stop(ending); // send destroy event to the VM
        }.bind(this)
      );
    }
    /**
     * Get the active Window
     * @returns {classes.WindowNode|null}
     */
    getVMWindow() {
      if (this.ending) {
        return null;
      }
      const uiNode = this.uiNode();
      const uiCurrentWindow = uiNode && uiNode.isAttributeSetByVM("currentWindow") && uiNode.attribute("currentWindow");
      if (uiCurrentWindow) {
        // we don't consider the value 0 as it is not a Window node
        return this.model.getNode(uiCurrentWindow);
      }
      return null;
    }

    getActionApplicationService() {
      return this.action;
    }

    /**
     *
     * @param {classes.WidgetBase} widget
     */
    attachRootWidget(widget) {
      this._ui.getWidget().addChildWidget(widget);
    }

    /**
     *
     * @returns {classes.UIApplicationService}
     */
    getUI() {
      return this._ui;
    }

    /**
     * Get items from the chromebar or from the applicationHostWidget if legacy mode enabled
     * @param {String} name
     * @return {*}
     */
    getMenu(name) {
      return context.ChromeActionService.getGbcMenuItem(name);
    }

    /**
     * The title of the application
     * ready to be displayed
     * @returns {string}
     */
    getTitle() {
      return this._title;
    }

    /**
     * Store the new title and notify listeners that the title have changed
     * @param {string} title New title of the application
     */
    setTitle(title) {
      this._title = title;
      this.emit(context.constants.widgetEvents.titleChanged, title);
    }

    /**
     * Ressource name of the application image (icon)
     * @returns {string}
     */
    getImage() {
      return this._image;
    }

    /**
     * Store the new image (icon) and notify listeners that the image have changed
     * @param {string} image New image of the application
     */
    setImage(image) {
      this._image = image;
      this.emit(context.constants.widgetEvents.iconChanged, image);
    }

    /**
     * flag to indicate if window list should be visible or not
     * @param {String} visible could be 'yes' (default) or 'no'
     */
    setWindowListVisible(visible) {
      this._windowListVisible = visible;
    }

    /**
     * Returns window list flag visibility
     * @return {Boolean}
     */
    isWindowListVisible() {
      return this._windowListVisible;
    }

    /**
     * Is multi-column sort supported
     * @return {Boolean}
     */
    isMultiColumnSortSupported() {
      return this.applicationInfo.connectionInfo.multiColumnSort === 1;
    }

    /**
     * get the application procId
     * @return {String}
     */
    getProcId() {
      return this.procId || this.info().procId || this.applicationInfo.connectionInfo.procId;
    }

    /**
     * Get the parent application procId
     * @return {String}
     */
    getProcIdParent() {
      return this.applicationInfo.connectionInfo.procIdParent;
    }

    /**
     * Get the waiting application procId
     * @return {String}
     */
    getProcIdWaiting() {
      return this.applicationInfo.connectionInfo.procIdWaiting;
    }

    /**
     * Returns True if a parent application is waiting for this application
     * @return {boolean}
     */
    isAwaited() {
      return Boolean(this.applicationInfo.taskWaiting);
    }

    /**
     * Return true if we can accept events on this application
     * @return {boolean}
     */
    canProcessEvent() {
      const curSession = context.SessionService.getCurrent();
      const navigationManager = curSession && curSession.getNavigationManager();

      return !(this.isProcessing() && navigationManager.isApplicationWaiting(this));
    }

    /**
     * attach to gbcReady event
     * @param {Function} hook the hook
     * @param {boolean} [once] true to free handle after first call
     * @return {HandleRegistration} the event handle
     */
    gbcReady(hook, once) {
      return this.when(context.constants.widgetEvents.gbcReady, hook, once);
    }
  }
  cls.VMApplication = VMApplication;
});
